import { useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import Pagination from "../../../../components/Pagination";
import BackButton from "../../../../components/BackButton";
import "../../Common/form.css";
import Table from "../../../../components/table";
import usePagination from "../../../../utils/usePagination";

const BankMaster = () => {
  const [banks, setBanks] = useState([
    {
      id: 1,
      account_name: "Naavvaayu Pvt Ltd",
      bank_name: "HDFC Bank",
      account_number: "1234567890",
      ifsc: "HDFC0001234",
      swift: "HDFCINBB",
      bank_address: "Noida, Uttar Pradesh",
    },
    {
      id: 2,
      account_name: "Naavvaayu Logistics",
      bank_name: "ICICI Bank",
      account_number: "2234567890",
      ifsc: "ICIC0001234",
      swift: "ICICINBB",
      bank_address: "Delhi",
    },
    {
      id: 3,
      account_name: "Naavvaayu Shipping",
      bank_name: "State Bank of India",
      account_number: "3234567890",
      ifsc: "SBIN0001234",
      swift: "SBININBB",
      bank_address: "Mumbai",
    },
    {
      id: 4,
      account_name: "Naavvaayu Transport",
      bank_name: "Axis Bank",
      account_number: "4234567890",
      ifsc: "UTIB0001234",
      swift: "AXISINBB",
      bank_address: "Gurugram",
    },
    {
      id: 5,
      account_name: "Naavvaayu International",
      bank_name: "Kotak Mahindra Bank",
      account_number: "5234567890",
      ifsc: "KKBK0001234",
      swift: "KKBKINBB",
      bank_address: "Delhi",
    },
    {
      id: 6,
      account_name: "Naavvaayu Services",
      bank_name: "Yes Bank",
      account_number: "6234567890",
      ifsc: "YESB0001234",
      swift: "YESBINBB",
      bank_address: "Noida",
    },
    {
      id: 7,
      account_name: "Naavvaayu Trading",
      bank_name: "Punjab National Bank",
      account_number: "7234567890",
      ifsc: "PUNB0001234",
      swift: "PUNBINBB",
      bank_address: "Ghaziabad",
    },
  ]);

  const [showModal, setShowModal] = useState(false);

  // add | edit | view
  const [modalMode, setModalMode] = useState("add");

  const [selectedBank, setSelectedBank] = useState(null);

  const [formData, setFormData] = useState({
    account_name: "",
    bank_name: "",
    account_number: "",
    ifsc: "",
    swift: "",
    bank_address: "",
  });

  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    currentItems,
    goToPage,
    handleItemsPerPageChange,
  } = usePagination(banks, 5);

  const resetForm = () => {
    setFormData({
      account_name: "",
      bank_name: "",
      account_number: "",
      ifsc: "",
      swift: "",
      bank_address: "",
    });
  };

  // =========================
  // ADD MODAL
  // =========================
  const handleOpenAdd = () => {
    setModalMode("add");
    setSelectedBank(null);
    resetForm();
    setShowModal(true);
  };

  // =========================
  // VIEW MODAL
  // =========================
  const handleOpenView = (bank) => {
    setModalMode("view");
    setSelectedBank(bank);

    setFormData({
      account_name: bank.account_name || "",
      bank_name: bank.bank_name || "",
      account_number: bank.account_number || "",
      ifsc: bank.ifsc || "",
      swift: bank.swift || "",
      bank_address: bank.bank_address || "",
    });

    setShowModal(true);
  };

  // =========================
  // EDIT MODAL
  // =========================
  const handleOpenEdit = (bank) => {
    setModalMode("edit");
    setSelectedBank(bank);

    setFormData({
      account_name: bank.account_name || "",
      bank_name: bank.bank_name || "",
      account_number: bank.account_number || "",
      ifsc: bank.ifsc || "",
      swift: bank.swift || "",
      bank_address: bank.bank_address || "",
    });

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedBank(null);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================
  // SAVE ADD / EDIT
  // =========================
  const handleSubmit = (e) => {
    e.preventDefault();

    if (modalMode === "add") {
      const newBank = {
        id: banks.length
          ? Math.max(...banks.map((item) => item.id)) + 1
          : 1,
        ...formData,
      };

      setBanks((prev) => [...prev, newBank]);
    }

    if (modalMode === "edit" && selectedBank) {
      setBanks((prev) =>
        prev.map((item) =>
          item.id === selectedBank.id
            ? {
                ...item,
                ...formData,
              }
            : item
        )
      );
    }

    handleCloseModal();
  };

  // =========================
  // VIEW -> EDIT
  // =========================
  const handleViewToEdit = () => {
    setModalMode("edit");
  };

  const columns = [
    {
      label: "Account Name",
      accessor: "account_name",
    },
    {
      label: "Bank Name",
      accessor: "bank_name",
    },
    {
      label: "Action",
      render: (row) => (
        <div className="d-flex justify-content-center gap-2">
          <button
            type="button"
            title="View"
            className="bank-action-btn"
            onClick={() => handleOpenView(row)}
          >
            <VisibilityIcon />
          </button>

          <button
            type="button"
            title="Edit"
            className="bank-action-btn"
            onClick={() => handleOpenEdit(row)}
          >
            <EditIcon />
          </button>
        </div>
      ),
    },
  ];

  const getModalTitle = () => {
    if (modalMode === "add") return "Add Bank";
    if (modalMode === "edit") return "Edit Bank";
    return "View Bank";
  };

  return (
    <>
      <BackButton />

      {/* Add Bank Button */}
      <div className="d-flex justify-content-end mb-3">
        <button
          type="button"
          className="btn bank-add-btn"
          onClick={handleOpenAdd}
        >
          + Add New Bank
        </button>
      </div>

      <Table
        columns={columns}
        data={currentItems}
        loading={false}
        emptyMessage="No bank details found."
        itemsPerPage={itemsPerPage}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalItems}
          />
        }
      />

      {/* ==============================
          BANK MODAL
      ============================== */}
      {showModal && (
        <div className="bank-modal-overlay">
          <div className="bank-modal-box">
            {/* Header */}
            <div className="bank-modal-header">
              <h4>{getModalTitle()}</h4>

              <button
                type="button"
                className="bank-modal-close"
                onClick={handleCloseModal}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit}>
              <div className="bank-modal-body">
                {/* Account Name */}
                <div className="bank-form-group">
                  <label>Account Name</label>

                  <input
                    type="text"
                    name="account_name"
                    value={formData.account_name}
                    onChange={handleChange}
                    placeholder="Enter Account Name"
                    readOnly={modalMode === "view"}
                  />
                </div>

                {/* Bank Name */}
                <div className="bank-form-group">
                  <label>Bank Name</label>

                  <input
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    placeholder="Enter Bank Name"
                    readOnly={modalMode === "view"}
                  />
                </div>

                {/* Account Number */}
                <div className="bank-form-group">
                  <label>Account Number</label>

                  <input
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleChange}
                    placeholder="Enter Account Number"
                    readOnly={modalMode === "view"}
                  />
                </div>

                {/* IFSC */}
                <div className="bank-form-group">
                  <label>IFSC</label>

                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleChange}
                    placeholder="Enter IFSC"
                    readOnly={modalMode === "view"}
                  />
                </div>

                {/* SWIFT */}
                <div className="bank-form-group">
                  <label>SWIFT</label>

                  <input
                    type="text"
                    name="swift"
                    value={formData.swift}
                    onChange={handleChange}
                    placeholder="Enter SWIFT Code"
                    readOnly={modalMode === "view"}
                  />
                </div>

                {/* Bank Address */}
                <div className="bank-form-group">
                  <label>Bank Address</label>

                  <textarea
                    name="bank_address"
                    value={formData.bank_address}
                    onChange={handleChange}
                    placeholder="Enter Bank Address"
                    readOnly={modalMode === "view"}
                    rows="3"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="bank-modal-footer">
                <button
                  type="button"
                  className="bank-cancel-btn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>

                {modalMode === "add" && (
                  <button type="submit" className="bank-primary-btn">
                    Add Bank
                  </button>
                )}

                {modalMode === "edit" && (
                  <button type="submit" className="bank-primary-btn">
                    Update Bank
                  </button>
                )}

                {modalMode === "view" && (
                  <button
                    type="button"
                    className="bank-primary-btn"
                    onClick={handleViewToEdit}
                  >
                    Edit
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .bank-add-btn {
          background: #1d3160;
          color: #fff;
          border: none;
          padding: 9px 18px;
          border-radius: 5px;
        }

        .bank-add-btn:hover {
          background: #15264d;
          color: #fff;
        }

        .bank-action-btn {
          background: transparent;
          border: none;
          padding: 0;
          cursor: pointer;
          color: #8393a2;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .bank-action-btn svg {
          font-size: 22px;
        }

        /* ==========================
           MODAL OVERLAY
        ========================== */

        .bank-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 20px;
        }

        /* ==========================
           MODAL BOX
        ========================== */

        .bank-modal-box {
          width: 625px;
          max-width: 100%;
          background: #fff;
          border-radius: 11px;
          overflow: hidden;
          box-shadow: 0 5px 25px rgba(0, 0, 0, 0.2);
        }

        /* ==========================
           HEADER
        ========================== */

        .bank-modal-header {
          min-height: 78px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 19px;
          border-bottom: 1px solid #d8dde3;
        }

        .bank-modal-header h4 {
          margin: 0;
          font-size: 25px;
          font-weight: 600;
          color: #252a30;
        }

        .bank-modal-close {
          border: none;
          background: transparent;
          color: #808080;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          cursor: pointer;
        }

        .bank-modal-close svg {
          font-size: 30px;
        }

        /* ==========================
           BODY
        ========================== */

        .bank-modal-body {
          padding: 27px 20px 25px;
          max-height: 68vh;
          overflow-y: auto;
        }

        .bank-form-group {
          margin-bottom: 22px;
        }

        .bank-form-group:last-child {
          margin-bottom: 0;
        }

        .bank-form-group label {
          display: block;
          margin-bottom: 7px;
          color: #34383d;
          font-weight: 600;
          font-size: 15px;
        }

        .bank-form-group input,
        .bank-form-group textarea {
          width: 100%;
          border: 1px solid #c9d4df;
          border-radius: 10px;
          background: #edf6fc;
          color: #333;
          font-size: 15px;
          padding: 11px 13px;
          outline: none;
          transition: 0.2s ease;
        }

        .bank-form-group input {
          height: 45px;
        }

        .bank-form-group textarea {
          resize: none;
          min-height: 80px;
        }

        .bank-form-group input::placeholder,
        .bank-form-group textarea::placeholder {
          color: #98a1ab;
        }

        .bank-form-group input:focus,
        .bank-form-group textarea:focus {
          border-color: #9eafc2;
          box-shadow: 0 0 0 2px rgba(29, 49, 96, 0.05);
        }

        /* View Mode */
        .bank-form-group input[readonly],
        .bank-form-group textarea[readonly] {
          cursor: default;
          background: #edf6fc;
        }

        /* ==========================
           FOOTER
        ========================== */

        .bank-modal-footer {
          border-top: 1px solid #e1e4e8;
          padding: 20px;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 10px;
        }

        .bank-cancel-btn {
          background: #737f8a;
          border: none;
          color: #fff;
          border-radius: 5px;
          padding: 10px 16px;
          font-size: 16px;
        }

        .bank-cancel-btn:hover {
          background: #626d77;
        }

        .bank-primary-btn {
          background: #1d3160;
          border: none;
          color: #fff;
          border-radius: 5px;
          padding: 10px 20px;
          font-size: 16px;
        }

        .bank-primary-btn:hover {
          background: #15264d;
        }

        /* ==========================
           RESPONSIVE
        ========================== */

        @media (max-width: 767px) {
          .bank-modal-overlay {
            align-items: flex-start;
            overflow-y: auto;
            padding: 20px 12px;
          }

          .bank-modal-box {
            margin-top: 20px;
          }

          .bank-modal-header h4 {
            font-size: 21px;
          }

          .bank-modal-body {
            max-height: none;
          }
        }
      `}</style>
    </>
  );
};

export default BankMaster;